import _ from 'lodash'
import Tooltip from 'scoreboard/components/tooltip'
import i18n from 'scoreboard/components/i18n'
import googleApi from 'scoreboard/components/google-api'
import WsConstants from 'scoreboard/admin/model/ws-constants'
const { WS_VENDOR_TYPE } = WsConstants

/**
 * Menu options of data source types which may be used when
 * importing data (ex. datasets, value imports, item imports)
 * @param model {Importable} the current model
 */
function dataSourceTypeMenuOptions(model) {
	let types = [{
		// spreadsheet options
		optionType: 'admin.import.datasource.spreadsheet',
		options: [{
			// Upload a file
			type: 'upload',
			label: 'admin.import.spreadsheet.source.upload',
			icon: 'upload-file',
			groupType: 'spreadsheet'
		}, {
			// Spider Impact file
			type: 'document',
			label: 'admin.import.spreadsheet.source.document',
			icon: 'spider-impact-file',
			groupType: 'spreadsheet'
		}, {
			// Google Sheets
			type: 'google',
			label: 'admin.import.spreadsheet.source.google',
			icon: 'google-sheets',
			groupType: 'spreadsheet'
		}, {
			// FTP
			type: 'ftp',
			label: 'admin.import.spreadsheet.source.sftp',
			icon: 'sftp',
			groupType: 'spreadsheet'
		}]
	}, {
		// database options
		optionType: 'admin.import.datasource.database',
		options: [{
			// MySQL
			type: 'mysql',
			label: 'admin.databaseConnections.type.mysql',
			icon: 'mysql',
			groupType: 'database'
		}, {
			// Microsoft SQL Server
			type: 'sqlserver',
			label: 'admin.databaseConnections.type.sqlserver',
			icon: 'microsoft-sql',
			groupType: 'database'
		}, {
			// Oracle
			type: 'oracle',
			label: 'admin.databaseConnections.type.oracle',
			icon: 'oracle',
			groupType: 'database'
		}, {
			// DB2
			type: 'db2',
			label: 'admin.databaseConnections.type.db2',
			icon: 'ibm',
			groupType: 'database'
		}, {
			// SAP HANA
			type: 'hana',
			label: 'admin.databaseConnections.type.sap',
			icon: 'sap-hana',
			groupType: 'database'
		}]
	}, {
		// other options
		optionType: 'admin.import.datasource.other',
		options: [{
			// Zapier
			type: 'zapier',
			label: 'admin.import.datasource.zapier',
			icon: 'zapier',
			groupType: 'spreadsheet'
		}, {
			// REST Web Service
			type: 'rest',
			label: 'admin.importConnections.web-service.restWebService',
			icon: 'rest-web-service',
			// saved as dataSourceMetadata.source for metric value imports, and
			// used by vi-wizard.js:getViews
			groupType: 'rest'
		/* #48218 Comment out JSON and SOAP import connections
		}, {
			// SOAP Web Service
			type: 'soap',
			label: 'admin.import.datasource.webservice.soap',
			icon: 'soap-web-service',
			groupType: 'soap'
			*/
		}, {
			type: WS_VENDOR_TYPE.ORACLE_NETSUITE,
			label: 'admin.importConnections.web-service.rest.vendor.oracleNetSuite',
			icon: 'oracle-netsuite',
			groupType: 'rest'
		}, {
			// None
			type: 'none',
			label: 'common.none',
			icon: 'layout-blank',
			groupType: 'other'
		}]
	}]

	// remove/modify options based on import type
	let importType = _.get(model.attributes, 'type')
	types.forEach(group => {
		if (importType !== 'dataset') {
			// only datasets should have the have blank/none choice
			group.options = _.filter(group.options, o => o.type != 'none')
		}
		// #48643 Google Sheets option should be hidden if not available
		if (!googleApi.isAvailable()) {
			group.options = _.filter(group.options, o => o.type != 'google')
		}
	})

	return types
}

/**
 * Array of data source types that are allowed for the given model
 * @param {Importable} model
 */
function importDataSourceTypes(model) {
	let menuOptionsForModel = dataSourceTypeMenuOptions(model)
	return toDataSourceTypes(menuOptionsForModel)
}

/**
 * Returns the given data source type menu options flattened to an array
 * @param {Object} menuOptions data source types (previously generated by
 * dataSourceTypeMenuOptions)
 */
function toDataSourceTypes(menuOptions) {
	return menuOptions.flatMap(type => type.options)
}

/**
 * Retrieves the dataSourceType given the 'type' property
 * @param {String} type data source type property (ex. 'upload' or 'sqlserver')
 * @param {Importable} model
 * @param {Object} [menuOptions]
 */
function byType (type, model, menuOptions) {
	if (_.isNil(menuOptions)) {
		menuOptions = dataSourceTypeMenuOptions(model)
	}
	let dataSourceType = toDataSourceTypes(menuOptions)
		.find(dst => dst.type === type)
	return dataSourceType
}

/** shows a tooltip if the selected data source type is not yet implemented */
function notImplementedTooltip(target, model, menuOptions) {
	let selectedType = target.data('type')
	let dataSourceType = byType(selectedType, model, menuOptions)
	if (dataSourceType.disabled) {
		// If the data source type is not yet supported, don't let it become selected
		return new Tooltip({
			attachTo: target,
			content: i18n.get('common.error.notYetImplemented'),
			placement: 'above',
			arrow: 'center'
		}).render()
	}
}

const types = importDataSourceTypes
const menuOptions = dataSourceTypeMenuOptions
const displayTooltipIfNotImplemented = notImplementedTooltip

export { types }
export { byType }
export { menuOptions }
export { displayTooltipIfNotImplemented }